import { ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { TASKS_STREETS_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

/**
 * What has been fetched, and what is on its way. Both are module-level rather than
 * per-caller: a screen can hold more than one of these - the map beside a form and the
 * panel beside a table are their own components - and each keeping its own copy meant
 * the same streets were fetched once per component. Shared, the first ask fetches and
 * the rest read what it brought.
 */
const streetsByTask = ref({})
const pendingByTask = {}
// Bumped whenever what is known about a task's streets is dropped. Screens that keep a
// list of their own - the route tab fetches its rows through the table - watch this to
// know that what they are showing is behind.
const streetsChangedAt = ref(0)

/**
 * The drawn streets of one or more tasks, for the screens that only want to look at
 * them on a map: the task's basic information tab and the listing's side map.
 *
 * The tasks listing reports no geometry, so the map cannot be fed from the rows the
 * table already has - each task's streets have to be fetched. The whole selection is
 * fetched in a single call: ticking the header checkbox picks a full page of tasks at
 * once, and one request per task made that a burst of them. Once fetched they are
 * remembered, so ticking a task off and back on again costs nothing.
 */
export function useTaskStreetPaths () {
  // Per caller rather than shared: the loader belongs to the panel that asked.
  const isFetching = ref(false)
  let fetchingCount = 0

  const normalizeIds = (taskIds) => (isArray(taskIds) ? taskIds : [])
    .map(id => Number(id))
    .filter(id => !!id)

  /**
   * The streets of every given task, in one request. Answers keyed by task, with a
   * key for every task asked for - a task with no streets of its own has to be
   * remembered as empty, or it would be fetched again on every tick.
   */
  const fetchTasksStreets = async (taskIds) => {
    const { data } = await api(WINTER_SERVICE_MODULE).post(TASKS_STREETS_ENDPOINT, {
      task_ids: taskIds
    })

    const collected = taskIds.reduce((byTask, id) => {
      byTask[id] = []

      return byTask
    }, {})

    if (isArray(data)) {
      data.forEach(street => {
        const taskId = Number(street?.task_id)

        if (collected[taskId]) {
          collected[taskId].push(street)
        }
      })
    }

    return collected
  }

  /**
   * Drops what is remembered about the given tasks, so the next ask fetches them
   * again. Used by `reload`, and by whoever changes a task's streets - the route tab
   * writes them through its own endpoints, which this never sees.
   */
  const forgetStreets = (taskIds) => {
    const ids = normalizeIds(taskIds)
    const kept = { ...streetsByTask.value }

    ids.forEach(id => {
      delete kept[id]
      delete pendingByTask[id]
    })

    streetsByTask.value = kept
    streetsChangedAt.value += 1
  }

  const fetchMissing = (ids) => {
    // One assignment rather than one per task: the map redraws on every change, and a
    // whole selection is a single change.
    const request = fetchTasksStreets(ids)
      .then(fetched => {
        streetsByTask.value = {
          ...streetsByTask.value,
          ...fetched
        }
      })
      .catch(exception => {
        console.error(exception)
      })
      .finally(() => {
        ids.forEach(id => {
          delete pendingByTask[id]
        })
      })

    ids.forEach(id => {
      pendingByTask[id] = request
    })
  }

  /**
   * The streets of every given task, fetching only the ones not seen yet. Answers
   * with them flattened, which is what the map panels take.
   *
   * `reload` fetches the given tasks again even if they are already remembered - what
   * is written about a street can change while the screen stays open, the colour it is
   * drawn in among it.
   */
  const loadStreets = async (taskIds, { reload = false } = {}) => {
    const ids = normalizeIds(taskIds)

    if (reload) {
      forgetStreets(ids)
    }

    const missing = ids.filter(id => streetsByTask.value[id] === undefined && !pendingByTask[id])

    if (missing.length) {
      fetchMissing(missing)
    }

    // Whatever is on its way for these tasks, whether it was started here or by a
    // caller that got in first.
    const inFlight = [...new Set(ids.map(id => pendingByTask[id]).filter(Boolean))]

    if (inFlight.length) {
      fetchingCount += 1
      isFetching.value = true

      try {
        await Promise.all(inFlight)
      }
      finally {
        fetchingCount -= 1
        isFetching.value = fetchingCount > 0
      }
    }

    return ids.flatMap(id => streetsByTask.value[id] || [])
  }

  return {
    isFetching,
    streetsChangedAt,
    loadStreets,
    forgetStreets
  }
}
